import { z } from 'zod'
import { zId, zDate, zIsoDate, zJsonBlob } from '../common'
import { zResearchRunStatus } from '../enums'

// Health metrics for a completed run. jsonb blob — keys stay snake_case. Every
// field is tolerant (optional) so a partial/evolving outcome never rejects.
export const zResearchOutcome = z
  .object({
    topics_created: z.number().optional(),
    articles_scheduled: z.number().optional(),
    refreshes_scheduled: z.number().optional(),
    skipped_duplicates: z.number().optional(),
    rejected_invalid: z.number().optional(),
    rejection_rate: z.number().optional(),
    details: z
      .array(
        z
          .object({
            type: z.string(),
            key: z.string(),
            action: z.string(),
            reason: z.string(),
          })
          .partial()
          .passthrough(),
      )
      .optional(),
  })
  .passthrough()
export type ResearchOutcome = z.infer<typeof zResearchOutcome>

// Lightweight list projection: no analyticsDigest / plan (heavy blobs).
export const zResearchRunListItem = z.object({
  id: zId,
  tenantId: zId,
  runOn: zDate,
  status: zResearchRunStatus,
  // 'gsc' | 'dataforseo' | 'llm_only' | 'skip' | null. Kept as a loose string
  // so an unseen mode never rejects the row.
  mode: z.string().nullable(),
  tokenCostCents: z.number().nullable(),
  error: z.string().nullable(),
  outcome: zResearchOutcome.nullable(),
  createdAt: zIsoDate,
  updatedAt: zIsoDate,
})
export type ResearchRunListItem = z.infer<typeof zResearchRunListItem>

// Full detail: list fields + the heavy diagnostic blobs. Both blobs keep their
// internal snake_case; the detail view reads into them defensively.
export const zResearchRunDetail = zResearchRunListItem.extend({
  analyticsDigest: zJsonBlob.nullable(),
  plan: zJsonBlob.nullable(),
})
export type ResearchRunDetail = z.infer<typeof zResearchRunDetail>
